// ────────────────────────────────────────────────────────────────────────────
// Which tax schema a charge line defaults to on an invoice.
//
// The VAT treatment matrix (Settings → Organisation) decides the TREATMENT
// from the customer's VAT country and the route. Only when it lands on the
// STANDARD line do the overrides come in — a reduced or zero rate for some
// goods, set on the cargo's product, the cargo definition or the charge:
//
//   charge's VAT follows the job:   product → cargo → charge → treatment
//   it does not:                    charge → treatment
//
// ("VAT follows the job" is its own switch on the charge, apart from the
// category one: waiting time takes the transport's VAT even when its category
// is fixed.)
//
// On any other line (zero-rated export, reverse charge, outside scope) the
// treatment's schema stands whatever the overrides say.
// ────────────────────────────────────────────────────────────────────────────
import type { OrganisationTaxRate, VatTreatmentLine } from '@my/directory-api';
import type { CargoDefinition, ChargeCostDefinition } from '@my/tms-api';

export type LineVatSource = 'treatment' | 'product' | 'cargo' | 'charge';

export interface LineVatResolution {
  /** The treatment line the sale fell on. */
  line: VatTreatmentLine;
  /** The schema to default to; undefined when the line has none set (amber). */
  rate: OrganisationTaxRate | undefined;
  /** Which level supplied `rate`. */
  source: LineVatSource;
  /**
   * The order carries products whose VAT rates disagree, so no product-level
   * rate was taken — the operator should check the line.
   */
  productsDisagree: boolean;
}

export function resolveLineVatRate(args: {
  treatment: { line: VatTreatmentLine; rate: OrganisationTaxRate | undefined } | undefined;
  charge: ChargeCostDefinition | undefined;
  cargo: CargoDefinition | undefined;
  productIds: readonly string[];
  ratesById: Map<string, OrganisationTaxRate>;
}): LineVatResolution | undefined {
  const { treatment, charge, cargo, productIds, ratesById } = args;
  if (!treatment) return undefined;

  const base: LineVatResolution = {
    line: treatment.line,
    rate: treatment.rate,
    source: 'treatment',
    productsDisagree: false,
  };
  // Overrides only ever change the RATE on the standard line.
  if (treatment.line.suggestedTaxType !== 'Standard') return base;

  // A schema since deleted or switched off is no override at all.
  const live = (id: string | null | undefined) => {
    const r = id ? ratesById.get(id) : undefined;
    return r && r.status === 'Active' ? r : undefined;
  };

  if (charge?.vatFollowsJob) {
    const cargoRate = live(cargo?.taxRateId);

    // Each product's effective rate: its own on this cargo, else the cargo's.
    const links = new Map((cargo?.products ?? []).map((p) => [p.productId, p]));
    const effective = productIds.map((id) => {
      const own = live(links.get(id)?.taxRateId);
      return { own, rate: own ?? cargoRate };
    });
    const anyOwn = effective.some((e) => e.own);
    if (anyOwn) {
      const ids = new Set(effective.map((e) => e.rate?.id ?? ''));
      const first = effective[0]?.rate;
      if (ids.size === 1 && first) return { ...base, rate: first, source: 'product' };
      // Products disagree — take no product rate; fall to the cargo / charge
      // and flag the line.
      base.productsDisagree = true;
    }
    if (cargoRate) return { ...base, rate: cargoRate, source: 'cargo' };
  }

  const chargeRate = live(charge?.taxRateId);
  if (chargeRate) return { ...base, rate: chargeRate, source: 'charge' };
  return base;
}
